<template>
  <v-container class="px-8">
    <div class="py-3 pl-2 flex-grow-0 flex-shrink-0">
      <div class="d-flex flex-row align-end">
        <div class="text-h4 font-weight-medium">
          {{ $t("navbar.menu.reviews") }}
        </div>
      </div>
    </div>
    <v-card max-width="70vw">
      <!-- Trip Survey Configuration -->
      <v-card-title class="headline">
        {{ $t("tripSurvey.title") }}
      </v-card-title>
      <v-card-text>
        <p class="text-body-1">
          {{
            isTripEnded
              ? $t("tripSurvey.descriptionEnded")
              : hasCustomSurvey
              ? $t("tripSurvey.descriptionCustom")
              : $t("tripSurvey.description")
          }}
        </p>
      </v-card-text>

      <!-- Survey Responses Section -->
      <template>
        <!-- Custom Survey Responses -->
        <v-card-actions v-if="customSurveyResponses && customSurveyResponses.length > 0">
          <v-btn
            v-if="customSurveyResponses.length > 0"
            color="primary"
            text
            @click="downloadCustomSurveyResponses"
          >
            <v-icon left>mdi-download</v-icon> {{ $t("tripSurvey.download") }}
          </v-btn>
        </v-card-actions>
        <v-list v-if="customSurveyResponses && customSurveyResponses.length > 0">
          <template v-for="response in customSurveyResponses">
            <v-list-item :key="response.userId">
              <v-list-item-avatar>
                <v-img :src="response.image"></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ response.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  <div v-for="(answer, index) in response.answers" :key="index" class="my-2">
                    <div class="font-weight-medium">{{ answer.question }}</div>
                    <div v-if="answer.type === 'rating'" class="d-flex flex-row">
                      <v-icon
                        v-for="i in 5"
                        :key="i"
                        :color="
                          answer.rating / 2 >= i
                            ? 'orange'
                            : answer.rating / 2 >= i - 0.5
                            ? 'orange'
                            : 'grey'
                        "
                        size="20"
                      >
                        {{
                          answer.rating / 2 >= i
                            ? "mdi-star"
                            : answer.rating / 2 >= i - 0.5
                            ? "mdi-star-half-full"
                            : "mdi-star-outline"
                        }}
                      </v-icon>
                      ({{ answer.rating }}/10)
                    </div>
                    <div v-else-if="answer.type === 'npsRating'">{{ answer.rating }}</div>
                    <div v-if="answer.type !== 'npsRating'">{{ answer.text }}</div>
                  </div>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-divider :key="`${response.userId}-divider`" />
          </template>
        </v-list>
        <!-- Default Survey Responses -->
        <v-list v-else-if="reviews.length > 0" three-line>
          <template v-for="review in reviews">
            <v-list-item :key="review.id">
              <v-list-item-avatar>
                <v-img :src="review.image"></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ review.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  <div class="d-flex flex-row my-2">
                    <v-icon
                      v-for="index in 5"
                      :key="index"
                      :color="index <= review.rating ? 'yellow' : 'grey'"
                      size="20"
                    >
                      mdi-star
                    </v-icon>
                  </div>
                  <div>{{ review.comments || $t("reviews.noReviewComments") }}</div>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-divider :key="`${review.userId}-divider`" />
          </template>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>
                {{ this.$t("reviews.average") }}: {{ averageRating }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <!-- No Responses Yet -->
        <v-card-text v-else>
          {{ this.$t("reviews.noReviewsText") }}
        </v-card-text>
      </template>

      <!-- Survey Configuration -->
      <!-- <v-card-actions>
        <template v-if="!isTripEnded">
          <v-btn
            v-if="!isEditingSurvey && !hasCustomSurvey"
            color="primary"
            @click="isEditingSurvey = true"
          >
            {{ $t("tripSurvey.customize") }}
          </v-btn>
          <v-btn v-if="hasCustomSurvey" color="primary" @click="isEditingSurvey = true">
            {{ $t("tripSurvey.edit") }}
          </v-btn>
        </template>
      </v-card-actions> -->
    </v-card>

    <!-- Survey Editor -->
    <TripSurvey
      v-if="isEditingSurvey && !isTripEnded"
      :initial-questions="surveyQuestions"
      @save="saveSurvey"
      @cancel="isEditingSurvey = false"
    />
    <div class="py-3 pl-2 flex-grow-0 flex-shrink-0">
      <div class="d-flex flex-row align-end">
        <div class="text-h4 font-weight-medium">
          <!-- {{ $t("navbar.menu.reviews") }} -->
          Itinerary Reviews
        </div>
      </div>
    </div>
    <v-card max-width="70vw">
      <template v-if="itineraryReviewsWithRatings.length > 0">
        <v-card-text>
          Download trip itinerary reviews of {{ itineraryReviewsWithRatings.length }} activities
        </v-card-text>

        <v-card-actions>
          <v-btn text color="primary" @click="downloadItineraryXlsx">
            <v-icon left>mdi-download</v-icon>Download
          </v-btn>
        </v-card-actions>
      </template>
      <template v-else>
        <v-card-title> No reviews for the itinerary </v-card-title>
        <v-card-text> No reviews have been submitted for the itinerary </v-card-text>
      </template>
    </v-card>
  </v-container>
</template>

<script>
import TripSurvey from "./TripSurvey.vue";
import firestore from "../../../firebase/firestore";

const Excel = require("exceljs");
const moment = require("moment-timezone");

export default {
  name: "Settings",
  components: { TripSurvey },
  data() {
    return {
      isEditingSurvey: false,
      defaultSurveyQuestions: [
        {
          type: "rating",
          title: this.$t("tripSurvey.defaultSurvey.rate"),
          description: this.$t("tripSurvey.defaultSurvey.rate"),
        },
        {
          type: "npsRating",
          title: this.$t("tripSurvey.defaultSurvey.recommendation"),
          description: this.$t("tripSurvey.defaultSurvey.recommendation"),
        },
        {
          type: "openText",
          title: this.$t("tripSurvey.defaultSurvey.recommendation_description"),
          description: this.$t("tripSurvey.defaultSurvey.recommendation_description"),
        },
      ],
    };
  },
  methods: {
    userShortName(id) {
      return this.$store.getters.userShortName(id) || "Unknown user";
    },
    sanitizeSheetName(name) {
      // Remove special characters that Excel doesn't allow
      const sanitized = name.replace(/[:\\/\\?*[\]]/g, "");

      // Trim to 31 characters (Excel's max length)
      return sanitized.substring(0, 20); // Default to "Sheet1" if empty
    },
    saveSurvey(questions) {
      // Update the trip survey in the store
      firestore.updateTrip(this.$store.state.trip.trip.id, {
        survey: {
          questions,
          responses: this.$store.state.trip.trip.survey?.responses || {},
        },
      });
      this.isEditingSurvey = false;
    },

    downloadCustomSurveyResponses() {
      const workbook = new Excel.Workbook();
      const worksheet = workbook.addWorksheet("Trip Survey");

      // Get questions from the survey
      const questions = this.hasCustomSurvey
        ? this.$store.state.trip.trip.survey.questions
        : this.defaultSurveyQuestions;

      // Set up columns - first column for respondent name, then one for each question
      const columns = [{ header: "Respondent", key: "name", width: 20 }];
      questions.forEach((q, i) => {
        columns.push({
          header: q.title,
          key: `q${i}`,
          width: q.type === "openText" ? 40 : 15,
        });
        if (q.type === "rating") {
          columns.push({
            header: `${q.title} comments`,
            key: `q${i}_comments`,
            width: 40,
          });
        }
      });

      worksheet.columns = columns;

      // Add response data
      this.customSurveyResponses.forEach((response) => {
        const rowData = {
          name: response.name,
        };

        // Add each answer
        response.answers.forEach((answer, index) => {
          if (answer.type === "rating" || answer.type === "npsRating") {
            rowData[`q${index}`] = answer.rating;
            rowData[`q${index}_comments`] = answer.text || "No response";
          } else {
            rowData[`q${index}`] = answer.text || "No response";
          }
        });

        worksheet.addRow(rowData);
      });

      // Style the header row
      worksheet.getRow(1).font = { bold: true };

      // Generate and download the file
      const { trip } = this.$store.state.trip;
      workbook.xlsx.writeBuffer().then((data) => {
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `${trip.name} survey responses.xlsx`;
        anchor.click();
        window.URL.revokeObjectURL(url);
      });
    },

    downloadItineraryXlsx() {
      const workbook = new Excel.Workbook();

      const takenNames = {};
      this.itineraryReviewsWithRatings.forEach((activity) => {
        if (!activity.ratings) return;
        const date = this.$options.filters
          .zonedDate(activity.sortingDate, "P", activity.display_timezone)
          .replaceAll("/", "-");
        let workSheetName = `${this.sanitizeSheetName(activity.name)}-${date}`;
        let nameIndex = takenNames[workSheetName];
        if (nameIndex) {
          nameIndex += 1;
          workSheetName = `${workSheetName} ${nameIndex}`;
          takenNames[workSheetName] = nameIndex;
        } else {
          takenNames[workSheetName] = 1;
        }
        const worksheet = workbook.addWorksheet(workSheetName);

        worksheet.columns = [
          { header: "Name", key: "name", width: 15 },
          { header: "Rating", key: "rating", width: 8 },
          { header: "Comments", key: "comments", width: 40 },
        ];

        Object.entries(activity.ratings).forEach(([key, review]) => {
          worksheet.addRow([
            this.userShortName(key),
            `${review.rating}/5`,
            review.comments || "No Comments",
          ]);
        });
      });
      console.log("creating document");
      const { trip } = this.$store.state.trip;
      workbook.xlsx.writeBuffer().then((data) => {
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `${trip.name} feedback.xlsx`;
        anchor.click();
        window.URL.revokeObjectURL(url);
      });
    },
  },
  computed: {
    isTripEnded() {
      // return false;
      const { trip } = this.$store.state.trip;
      const endDate = trip.end_date.toDate();
      return endDate < new Date();
    },
    hasCustomSurvey() {
      return this.$store.state.trip.trip.survey && this.$store.state.trip.trip.survey.questions;
    },
    surveyQuestions() {
      return this.hasCustomSurvey ? this.$store.state.trip.trip.survey.questions : [];
    },
    customSurveyResponses() {
      const { survey } = this.$store.state.trip.trip;
      if (!survey || !survey.responses) return [];

      const questions = this.hasCustomSurvey ? survey.questions : this.defaultSurveyQuestions;

      return Object.keys(survey.responses).map((userId) => {
        const user = this.$store.getters.getUserById(userId);
        const userResponses = survey.responses[userId];

        const answers = [];
        for (let i = 0; i < questions.length; i += 1) {
          const question = questions[i];
          const userResponse = userResponses[i];
          answers.push({
            question: question.title,
            type: question.type,
            rating: userResponse?.rating,
            text: userResponse?.text,
          });
        }

        return {
          userId,
          name: user.name,
          image: this.$store.getters.getImageUrlById(userId),
          answers,
        };
      });
    },
    itineraryReviewsWithRatings() {
      return this.$store.getters.sortedItinerary.filter((activity) => activity.ratings);
    },
    reviews() {
      const { ratings } = this.$store.state.trip.trip;

      return Object.keys(ratings || {})
        .map((userId) => {
          const user = this.$store.getters.getUserById(userId);
          const review = ratings[userId];
          return {
            userId,
            name: user.name,
            image: this.$store.getters.getImageUrlById(userId),
            rating: review.rating,
            comments: review.comments,
          };
        })
        .filter((review) => review.rating > 0);
    },
    averageRating() {
      const ratings = Object.values(this.$store.state.trip.trip.ratings).filter(
        (rating) => rating.rating > 0,
      );

      const total = ratings.reduce((acc, review) => acc + review.rating, 0);
      return (total / ratings.length).toFixed(1);
    },
  },
};
</script>
